import React from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvestigationPanel = ({ investigations, onViewDetails }) => {
  const getStatusColor = (status) => {
    const colors = {
      initiated: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      ongoing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors?.[status] || colors?.ongoing;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-600 dark:text-red-400',
      major: 'text-orange-600 dark:text-orange-400',
      moderate: 'text-amber-600 dark:text-amber-400',
      minor: 'text-yellow-600 dark:text-yellow-400'
    };
    return colors?.[severity] || colors?.moderate;
  };

  const getIncidentTypeLabel = (type) => {
    const labels = {
      'near-miss': 'Near Miss',
      'injury': 'Injury',
      'fatality': 'Fatality'
    };
    return labels?.[type] || type;
  };

  return (
    <div className="space-y-4">
      {investigations?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">No investigations found matching your filters</p>
        </div>
      ) : (
        investigations?.map(investigation => (
          <div key={investigation?.id} className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-sm font-semibold text-primary">
                    {investigation?.incidentId}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(investigation?.status)}`}>
                    {investigation?.status?.toUpperCase()}
                  </span>
                  <span className="px-2 py-1 rounded bg-muted text-muted-foreground text-xs font-medium">
                    {getIncidentTypeLabel(investigation?.incidentType)}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {investigation?.title}
                </h3>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="User" size={14} />
                    <span>{investigation?.investigator}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="MapPin" size={14} />
                    <span>{investigation?.location?.replace('-', ' ')?.toUpperCase()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Calendar" size={14} />
                    <span>Incident: {format(new Date(investigation?.incidentDate), 'MMM dd, yyyy')}</span>
                  </div>
                  <div className={`flex items-center gap-1 ${getSeverityColor(investigation?.severity)}`}>
                    <Icon name="AlertCircle" size={14} />
                    <span className="font-medium">{investigation?.severity?.toUpperCase()}</span>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                onClick={() => onViewDetails(investigation?.id)}
              >
                View Details
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Progress</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-background rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${investigation?.progress}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-foreground">{investigation?.progress}%</span>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Recommendations</p>
                <p className="text-sm font-semibold text-foreground">
                  {investigation?.implementedRecommendations}/{investigation?.recommendations} implemented
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Evidence</p>
                <div className="flex items-center gap-2">
                  <Icon name="FileText" size={14} className="text-muted-foreground" />
                  <span className="text-sm font-semibold text-foreground">
                    {investigation?.documentsCollected} documents
                  </span>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Interviews</p>
                <div className="flex items-center gap-2">
                  <Icon name="Users" size={14} className="text-muted-foreground" />
                  <span className="text-sm font-semibold text-foreground">
                    {investigation?.interviewsCompleted} completed
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Icon 
                    name={investigation?.rootCauseIdentified ? 'CheckCircle' : 'Circle'} 
                    size={14} 
                    className={investigation?.rootCauseIdentified ? 'text-emerald-600' : 'text-muted-foreground'} 
                  />
                  <span className="text-muted-foreground">Root Cause</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Icon name="Clock" size={14} />
                  <span>Target: {format(new Date(investigation?.targetCompletionDate), 'MMM dd, yyyy')}</span>
                </div>
              </div>
              {investigation?.status === 'completed' && investigation?.completionDate && (
                <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm">
                  <Icon name="CheckCircle" size={14} />
                  <span>Completed: {format(new Date(investigation?.completionDate), 'MMM dd, yyyy')}</span>
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default InvestigationPanel;